import { pascalCase } from 'change-case';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import { getCanonicalPartnerName, PartnerName } from '../../../../common/constants';
import { PartnerEnrollType } from '../../../../common/enums';
import { PartnerSpecificFormData, usePartnerSpecificFormConfigMap } from './PartnerSpecificFormConfig';
import { getMemberInformation } from '../../../../store/thunks/onboardingThunk';
import {
  enrollCompanyToPartner,
  getCompanyPartnerRelations,
} from '../../../../store/thunks/dashboardThunk';
import { usePartnerConfigList } from './usePartnerConfigList';

interface FinishPartnerEnrollmentProps {
  updateConfirmationDialogOpenState?: (event: boolean) => void;
  updateEnrollmentDialogOpenState?: (event: boolean) => void;
  partnerName: PartnerName;
  partnerCode: string;
  setErrorMessage?: (event: string) => void;
  setSnackbarOpen?: (event: boolean) => void;
  setLoading?: (event: boolean) => void;
  manufacturerName?: PartnerName;
  manufacturerCode?: string;
}

export function useFinishPartnerEnrollment({
  updateConfirmationDialogOpenState,
  updateEnrollmentDialogOpenState,
  partnerName,
  partnerCode,
  manufacturerName,
  manufacturerCode,
  setErrorMessage,
  setSnackbarOpen,
  setLoading,
}: FinishPartnerEnrollmentProps) {
  const dispatch = useAppDispatch();
  const { trackPartnerEnrollment, trackRequest } = useCustomEventTracking();
  const memberInfo = useAppSelector(selectMemberInformation);
  const partnerConfigList = usePartnerConfigList();
  const isSubmittingRef = useRef(false);
  const { t } = useTranslation();

  const partnerSpecificFormConfigMap = usePartnerSpecificFormConfigMap();
  const partnerConfig = useMemo(() => partnerSpecificFormConfigMap[partnerCode], [
    partnerSpecificFormConfigMap, partnerCode,
  ]);

  const finishEnrollment = async (data: PartnerSpecificFormData | null) => {
    // Prevent duplicate submissions
    if (isSubmittingRef.current) {
      return;
    }

    try {
      // Resolve partner ids from the stable partner code (SN-838): the code
      // never changes on a rename, so lookups keyed off it stay correct.
      const distributorId = partnerConfigList.find((p) => p.partnerCode &&
      p.partnerCode === partnerCode)?.id;
      const manufacturerId = partnerConfigList.find((p) => p.partnerCode &&
      p.partnerCode === manufacturerCode)?.id;
      const companyId = memberInfo.company?.id;

      if (!companyId || !distributorId) {
        throw new Error('Company or partner not found');
      }

      isSubmittingRef.current = true;
      setLoading?.(true);

      // Enroll company with the distributor by stable partner id.
      await dispatch(enrollCompanyToPartner({
        companyId,
        partnerId: distributorId,
        data: data ? {
          type: PartnerEnrollType.QuestionForm,
          enrollData: { ...data },
        } : {
          type: PartnerEnrollType.DirectEnroll,
          enrollData: null,
        },
      })).then(unwrapResult);

      if (manufacturerName) {
        if (!manufacturerId) {
          throw new Error('Manufacturer not found');
        }
        await dispatch(enrollCompanyToPartner({
          companyId,
          partnerId: manufacturerId,
          data: {
            type: PartnerEnrollType.Manufacturer,
            enrollData: { distributorId },
          },
        })).then(unwrapResult);
      }

      updateConfirmationDialogOpenState?.(true);
      if (partnerConfig?.hasExtraEnrollmentSteps) {
        // Continue to show dialog for extra steps but skip the confirmation
        updateConfirmationDialogOpenState?.(false);
        updateEnrollmentDialogOpenState?.(true);
      }
      dispatch(getMemberInformation({ trackRequest }));
      dispatch(getCompanyPartnerRelations(companyId));

      // fire event to indicate member has signed up with distributor (used for webhook)
      // Event NAME is derived from the stable code so it never changes on a rename.
      trackPartnerEnrollment('Submit', `${pascalCase(getCanonicalPartnerName(partnerCode, partnerName))}SignupForm`, {
        formData: data,
        isOptimizelyEvent: true,
      });
      // fire event to indicate company submitted distributor enrollment
      trackPartnerEnrollment('Submit', 'Form', {
        partnerName,
        enrollmentType: 'New',
        isOptimizelyEvent: true,
      });

      if (manufacturerName) {
        // fire event to indicate member has signed up with manufacturer as well (used for webhook)
        trackPartnerEnrollment('Submit', `${pascalCase(getCanonicalPartnerName(manufacturerCode, manufacturerName))}SignupForm`, {
          formData: null,
          isOptimizelyEvent: true,
          distributorEnrolledWith: partnerName,
        });
        // fire event to indicate company submitted manufacturer enrollment as well
        trackPartnerEnrollment('Submit', 'Form', {
          partnerName: manufacturerName,
          enrollmentType: 'New',
          isOptimizelyEvent: true,
        });
      }

      setLoading?.(false);
      isSubmittingRef.current = false;
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage?.(error.message);
      } else {
        setErrorMessage?.(t('error.generalMessage3'));
      }
      setSnackbarOpen?.(true);
      setLoading?.(false);
      isSubmittingRef.current = false;
    }
  };

  return finishEnrollment;
}
